import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { StatusBadge, MAINT_TYPES } from "@/lib/status";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Loader2, Wrench, Trash2, FileDown } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { asset_id: "", title: "", type: "Service", description: "", scheduled_date: "", performed_by: "", cost: "", completed: false };

export default function Maintenance() {
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [assets, setAssets] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => api.listMaint().then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); api.listAssets().then(setAssets).catch(() => {}); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const create = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    setBusy(true);
    try { await api.createMaint({ ...form, asset_id: form.asset_id === "none" ? "" : form.asset_id }); toast.success("Scheduled"); setOpen(false); setForm(EMPTY); load(); }
    catch { toast.error("Failed"); } finally { setBusy(false); }
  };
  const toggle = async (m) => {
    try { await api.updateMaint(m.id, { ...m, completed: !m.completed }); load(); }
    catch { toast.error("Update failed"); }
  };
  const del = async (m) => { try { await api.deleteMaint(m.id); toast.success("Removed"); load(); } catch { toast.error("Delete failed"); } };

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-8">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-primary">Servicing</span>
          <h1 className="mt-1 text-2xl font-bold tracking-tight sm:text-3xl">Maintenance</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="maint-pdf-btn" onClick={() => nav("/reports/maintenance")} variant="outline" className="gap-2 tap-scale"><FileDown className="h-4 w-4" /> <span className="hidden sm:inline">PDF</span></Button>
          <Button data-testid="new-maint-btn" onClick={() => { setForm(EMPTY); setOpen(true); }} className="gap-2 bg-primary text-primary-foreground tap-scale hover:bg-primary/90"><Plus className="h-4 w-4" /> Schedule</Button>
        </div>
      </div>

      {items === null ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border py-16 text-center">
          <Wrench className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">No maintenance jobs yet.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((m) => (
            <div key={m.id} data-testid={`maint-row-${m.id}`} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3.5 sm:p-4">
              <Checkbox data-testid={`maint-toggle-${m.id}`} checked={!!m.completed} onCheckedChange={() => toggle(m)} className="h-5 w-5 shrink-0 data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500" />
              <div className="min-w-0 flex-1">
                <div className={`text-sm font-medium ${m.completed ? "text-muted-foreground line-through" : "text-foreground"}`}>{m.title}</div>
                <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                  <span className="font-mono">{m.type}</span>
                  {m.asset_name && <span>· {m.asset_code} {m.asset_name}</span>}
                  {m.scheduled_date && <span>· due {m.scheduled_date}</span>}
                  {m.cost && <span>· {m.cost}</span>}
                </div>
              </div>
              <StatusBadge value={m.derived_status} testid={`maint-status-${m.id}`} />
              <Button variant="ghost" size="icon" onClick={() => del(m)} className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Schedule maintenance</DialogTitle><DialogDescription>Plan a service, inspection or repair.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div><Label className="mb-1.5 block text-xs text-muted-foreground">Title *</Label><Input data-testid="maint-title" value={form.title} onChange={(e) => set("title", e.target.value)} className="bg-secondary/40" placeholder="e.g. Annual PAT test" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="mb-1.5 block text-xs text-muted-foreground">Type</Label><Select value={form.type} onValueChange={(v) => set("type", v)}><SelectTrigger data-testid="maint-type" className="bg-secondary/40"><SelectValue /></SelectTrigger><SelectContent>{MAINT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="mb-1.5 block text-xs text-muted-foreground">Scheduled date</Label><Input data-testid="maint-date" type="date" value={form.scheduled_date} onChange={(e) => set("scheduled_date", e.target.value)} className="bg-secondary/40" /></div>
            </div>
            <div><Label className="mb-1.5 block text-xs text-muted-foreground">Asset (optional)</Label>
              <Select value={form.asset_id || "none"} onValueChange={(v) => set("asset_id", v)}>
                <SelectTrigger data-testid="maint-asset" className="bg-secondary/40"><SelectValue placeholder="No asset" /></SelectTrigger>
                <SelectContent><SelectItem value="none">No asset</SelectItem>{assets.map((a) => <SelectItem key={a.id} value={a.id}>{a.asset_code} · {a.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="mb-1.5 block text-xs text-muted-foreground">Performed by</Label><Input value={form.performed_by} onChange={(e) => set("performed_by", e.target.value)} className="bg-secondary/40" /></div>
              <div><Label className="mb-1.5 block text-xs text-muted-foreground">Cost</Label><Input value={form.cost} onChange={(e) => set("cost", e.target.value)} className="bg-secondary/40" placeholder="£" /></div>
            </div>
            <div><Label className="mb-1.5 block text-xs text-muted-foreground">Notes</Label><Textarea value={form.description} onChange={(e) => set("description", e.target.value)} className="bg-secondary/40" /></div>
            <Button data-testid="create-maint-confirm" onClick={create} disabled={busy} className="w-full gap-2 bg-primary text-primary-foreground tap-scale hover:bg-primary/90">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Schedule</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
